import { AttachmentCategory, UserProfileRole } from '@@types/api.types';
import { ProductsGridTile } from '@components/pages/product/components/products-grid-tile.component';
import { AttachmentsTile } from '@components/shared/attachments/attachments-tile.component';
import { getTechnologyAttachmentList } from '@components/shared/attachments/technology-attachments.util';
import { useGetTechnologyAttachments } from '@queries/attachments.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { FC } from 'react';
import { TechnologyContributorsTile } from './technology-contributors-tile.component';
import { TechnologyDetailsTile } from './technology-details-tile.component';
import { TechnologyReferencesTile } from './technology-research-tile.component';
import { TechnologySubsystems } from './technology-subsystems.component';
import { TechnologyUses } from './technology-uses.component';

type TechnologyMetadataTabProps = {
  technologyId?: string;
};

export const TechnologyMetadataTab: FC<TechnologyMetadataTabProps> = (props) => {
  const { technologyId } = props;

  const { data: technology } = useGetTechnology(technologyId);
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const { data: generalAttachments = [], isLoading: isGeneralAttachmentsLoading } = useGetTechnologyAttachments({
    category: AttachmentCategory.GENERAL,
    technologyId: technologyId ?? '',
  });
  const { data: technologyImages = [], isLoading: isTechnologyImagesLoading } = useGetTechnologyAttachments({
    category: AttachmentCategory.TECHNOLOGY_IMAGE,
    technologyId: technologyId ?? '',
  });

  const attachments = getTechnologyAttachmentList({
    generalAttachments,
    technologyImages,
  });
  const isAttachmentsLoading = isGeneralAttachmentsLoading || isTechnologyImagesLoading;
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEditAttachments = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  return (
    <div className="@container/technology-details">
      <div className="grid grid-cols-1 gap-4 @min-[40.625rem]/technology-details:grid-cols-2 @min-[62.5rem]/technology-details:grid-cols-3">
        {/* Column 1: Details + Contributors + Research */}
        <div className="flex flex-col gap-4">
          <TechnologyDetailsTile />
          <TechnologyContributorsTile />
          <TechnologyReferencesTile technologyId={technologyId} />
        </div>

        {/* Column 2: Technology Uses */}
        <div className="flex flex-col gap-4">
          <TechnologyUses />
        </div>

        {/* Column 3: Subsystems + Products + Attachments */}
        <div className="flex flex-col gap-4">
          <TechnologySubsystems />
          <ProductsGridTile
            hideIfEmpty={false}
            title="Related Products"
            technologyId={technologyId}
          />
          <AttachmentsTile
            allowTechnologyImages
            attachments={attachments}
            canEdit={canEditAttachments}
            category={AttachmentCategory.GENERAL}
            isAttachmentsLoading={isAttachmentsLoading}
            targetId={technologyId}
            targetType="technology"
          />
        </div>
      </div>
    </div>
  );
};
